import { Link } from "react-router-dom";
import { BarChart3, MapPin, Mail, Phone } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const Footer = () => {
  const { t, lang } = useI18n();
  const base = `/${lang}`;

  return (
    <footer
      data-testid="site-footer"
      className="bg-slate-950 text-slate-300 border-t border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-emerald-500/15 border border-emerald-500/30">
                <BarChart3 className="w-4 h-4 text-emerald-400" strokeWidth={2.4} />
              </span>
              <span className="font-display text-xl font-bold text-white">
                {t("footer.about_title")}
              </span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              {t("footer.about_text")}
            </p>
          </div>

          <div>
            <h4 className="font-display text-white text-sm font-semibold mb-4 uppercase tracking-widest">
              {t("footer.nav_title")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={base}
                  data-testid="footer-nav-home"
                  className="text-slate-300 hover:text-emerald-400"
                >
                  {t("nav.home")}
                </Link>
              </li>
              <li>
                <a
                  href={`${base}#about`}
                  data-testid="footer-nav-about"
                  className="text-slate-300 hover:text-emerald-400"
                >
                  {t("nav.about")}
                </a>
              </li>
              <li>
                <a
                  href={`${base}#how`}
                  data-testid="footer-nav-how"
                  className="text-slate-300 hover:text-emerald-400"
                >
                  {t("nav.how")}
                </a>
              </li>
              <li>
                <a
                  href={`${base}#faq`}
                  data-testid="footer-nav-faq"
                  className="text-slate-300 hover:text-emerald-400"
                >
                  {t("nav.faq")}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-white text-sm font-semibold mb-4 uppercase tracking-widest">
              {t("footer.legal_title")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={`${base}/privacy`}
                  data-testid="footer-privacy-link"
                  className="text-slate-300 hover:text-emerald-400"
                >
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${base}/cookies`}
                  data-testid="footer-cookie-policy-link"
                  className="text-slate-300 hover:text-emerald-400"
                >
                  {t("footer.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${base}/terms`}
                  data-testid="footer-terms-link"
                  className="text-slate-300 hover:text-emerald-400"
                >
                  {t("footer.terms")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-white text-sm font-semibold mb-4 uppercase tracking-widest">
              {t("footer.contact_title")}
            </h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                <span data-testid="footer-address" className="text-slate-400">
                  {t("footer.address")}
                </span>
              </li>
              <li className="flex gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                <a
                  href={`tel:${t("footer.phone").replace(/\s/g, "")}`}
                  data-testid="footer-phone"
                  className="text-slate-300 hover:text-emerald-400"
                >
                  {t("footer.phone")}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                <a
                  href={`mailto:${t("footer.email")}`}
                  data-testid="footer-email"
                  className="text-slate-300 hover:text-emerald-400"
                >
                  {t("footer.email")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-slate-800 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-xs text-slate-500 space-y-1.5">
            <p>
              <span className="text-slate-400">{t("footer.legal_name_label")}: </span>
              <span data-testid="footer-legal-name" className="text-slate-300">
                {t("footer.legal_name")}
              </span>
            </p>
            <p>
              <span className="text-slate-400">{t("footer.ruc_label")}: </span>
              <span data-testid="footer-ruc" className="text-slate-300 font-mono">
                {t("footer.ruc")}
              </span>
            </p>
            <p>
              <span className="text-slate-400">{t("footer.registry_label")}: </span>
              <span data-testid="footer-registry" className="text-slate-300">
                {t("footer.registry")}
              </span>
            </p>
            <p>
              <span className="text-slate-400">{t("footer.license_label")}: </span>
              <span data-testid="footer-license" className="text-slate-300">
                {t("footer.license")}
              </span>
            </p>
            <p>
              <span className="text-slate-400">{t("footer.jurisdiction_label")}: </span>
              <span data-testid="footer-jurisdiction" className="text-slate-300">
                {t("footer.jurisdiction")}
              </span>
              <span className="text-slate-500"> · </span>
              <span className="text-slate-400">{t("footer.founded_label")}: </span>
              <span data-testid="footer-founded" className="text-slate-300">
                {t("footer.founded")}
              </span>
            </p>
          </div>
          <div className="text-xs text-slate-500 md:text-right space-y-2">
            <p data-testid="footer-copyright">{t("footer.copyright")}</p>
            <p data-testid="footer-disclaimer" className="italic">{t("footer.disclaimer")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
